import styles from "./SectionTitle.module.scss";
import React, { ReactElement } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { QueryKey } from "../../models/enums";
import { IndexerService } from "../../services/indexer.service";
import { NetworkData } from "../../models/NetworkData";
import {
  IconClockHour9,
  IconCoins,
  IconCpu,
  IconLink,
  IconRosetteDiscountCheck,
  IconTimelineEvent,
  IconUsers,
  IconWall,
} from "@tabler/icons-react";
import { ValidatorsInfo } from "../../models/Validator";
import { formatGweiMillions, formatNumber } from "../../utils/formatter";
import Button from "../Button/Button.component";

export default function SectionTitle(): ReactElement | null {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;

  const navigateHome = () => {
    navigate("/");
  };

  const navigateToValidators = () => {
    navigate("/validators");
  };

  const { data: networkData } = useQuery({
    queryKey: [QueryKey.networkData],
    queryFn: async (): Promise<NetworkData> => IndexerService.getNetworkData(),
    refetchInterval: 5000,
    enabled: pathname === "/",
  });

  const { data: validatorsInfo } = useQuery({
    queryKey: [QueryKey.validatorList],
    queryFn: (): Promise<ValidatorsInfo> => IndexerService.getValidatorList(),
    enabled: pathname === "/validators" || pathname === "/",
    staleTime: 10000,
  });

  const totalStakedM = formatGweiMillions(validatorsInfo?.total);
  const rewardCapM = formatGweiMillions(validatorsInfo?.rewardCap);

  function mapShorterEnvTitles(env: string): string {
    switch (env) {
      case "production":
        return "";
      case "staging":
        return "TEST";
      case "development":
        return "DEV";
      default:
        return env.toUpperCase();
    }


  }

  return (
    <>
      <div className={styles.header}>
        <h1 onClick={navigateHome}>
          {pathname === "/validators" ? "Validators" : "P-Chain explorer"}
          {`${process.env.REACT_APP_DEPLOY_TARGET !== "production" ? " - " : ""}${mapShorterEnvTitles(process.env.REACT_APP_DEPLOY_TARGET as string)}`}
        </h1>
        {pathname === "/" && (
          <>
            <div
              className={`${styles.dataInfoWrapper} ${styles.transactionData}`}
            >
              <div className={styles.dataInfoBlocksWrapper}>
                <div className={styles.dataInfoBlocks}>
                  <div className={styles.greyBox}>
                    <IconLink />
                  </div>
                  <div className={styles.dataInfoBlocksText}>
                    <span>ADDRESSES</span>
                    <span>
                    {networkData?.numberOfAddresses
                      ? formatNumber(networkData.numberOfAddresses,false,0)
                      : "n/a"}
                  </span>
                  </div>
                </div>
                <div className={styles.dataInfoBlocks}>
                  <div className={styles.greyBox}>
                    <IconWall />
                  </div>
                  <div className={styles.dataInfoBlocksText}>
                    <span>BLOCKS</span>
                    <span>
                    {networkData?.numberOfBlocks
                      ? formatNumber(networkData.numberOfBlocks,false,0)
                      : "n/a"}
                  </span>
                  </div>
                </div>
                <div className={styles.dataInfoBlocks}>
                  <div className={styles.greyBox}>
                    <IconTimelineEvent />
                  </div>
                  <div className={styles.dataInfoBlocksText}>
                    <span>TRANSACTIONS</span>
                    <span>
                    {networkData?.numberOfTransactions
                      ? formatNumber(networkData.numberOfTransactions,false,0)
                      : "n/a"}
                  </span>
                  </div>
                </div>
                <div
                  className={`${styles.dataInfoBlocks} ${styles.outlineElement}`}
                >
                  <div className={styles.greyBox}>
                    <IconCpu />
                  </div>
                  <div className={styles.dataInfoBlocksText}>
                    <span>VALIDATORS</span>
                    <span>
                    {validatorsInfo?.totalNumberValidators
                      ? formatNumber(validatorsInfo.totalNumberValidators,false,0)
                      : "n/a"}
                  </span>
                  </div>
                  <div style={{ fontWeight: 500, marginLeft: "1rem" }}>
                    <Button
                      addStyles={["compact", "basic"]}
                      onClick={navigateToValidators}
                    >
                      VIEW
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {pathname === "/validators" && (
          <>
            <div className={`${styles.dataInfoWrapper} ${styles.validatorsData}`}>
              <div className={styles.dataInfoBlocksWrapper}>
                <div className={styles.dataInfoBlocks}>
                  <div className={styles.greyBox}>
                    <IconCpu />
                  </div>
                  <div className={styles.dataInfoBlocksText}>
                    <span>VALIDATORS</span>
                    <span>
                    {validatorsInfo?.totalNumberValidators
                      ? validatorsInfo.totalNumberValidators
                      : "n/a"}
                  </span>
                  </div>
                </div>
                <div className={styles.dataInfoBlocks}>
                  <div className={styles.greyBox}>
                    <IconUsers />
                  </div>
                  <div className={styles.dataInfoBlocksText}>
                    <span>DELEGATORS</span>
                    <span>
                    {validatorsInfo?.totalNumberDelegators
                      ? validatorsInfo.totalNumberDelegators
                      : "n/a"}
                  </span>
                  </div>
                </div>
                <div className={styles.dataInfoBlocks}>
                  <div className={styles.greyBox}>
                    <IconCoins />
                  </div>
                  <div className={styles.dataInfoBlocksText}>
                    <span>TOTAL STAKED</span>
                    <span>
                    {totalStakedM ? `${totalStakedM}M` : "n/a"}
                  </span>
                  </div>
                </div>
                <div className={styles.dataInfoBlocks}>
                  <div className={styles.greyBox}>
                    <IconRosetteDiscountCheck />
                  </div>
                  <div className={styles.dataInfoBlocksText}>
                    <span>REWARD CAP</span>
                    <span>
                    {rewardCapM ? `${rewardCapM}M` : "n/a"}
                  </span>
                  </div>
                </div>
                {" "}
                <div className={styles.dataInfoBlocks}>
                  <div className={styles.greyBox}>
                    <IconClockHour9 />
                  </div>
                  <div className={styles.dataInfoBlocksText}>
                    <span>CURRENT EPOCH</span>
                    <span>
                    {validatorsInfo?.validators[0]?.currentEpoch
                      ? validatorsInfo?.validators[0]?.currentEpoch
                      : "n/a"}
                  </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      {process.env.REACT_APP_CHAIN?.toLowerCase() === "flr" ? (
        <promo-banner></promo-banner>
      ) : (
        <div className={styles.bannerPlaceholder}></div>
      )}
    </>
  );
}
